import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import { useLanguage } from '../LanguageContext';
import Spinner from '../components/Spinner';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { reportsAPI, usersAPI } from '../services/api';
import MonthlyRevenueBreakdown from '../components/MonthlyRevenueBreakdown';

export default function DashboardPage() {
    const { currentUser } = useAuth();
    const { t, language } = useLanguage();
    const [chartData, setChartData] = useState([]);
    const [stats, setStats] = useState({ today: 0, week: 0 });
    const [trialCount, setTrialCount] = useState(0);
    const [mostAbsent, setMostAbsent] = useState([]);
    const [superAdminStats, setSuperAdminStats] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [analyticsLoading, setAnalyticsLoading] = useState(false);
    const [activeRevenueTab, setActiveRevenueTab] = useState(null);
    const [teachers, setTeachers] = useState([]);
    const [analyticsTeacherFilter, setAnalyticsTeacherFilter] = useState('');

    const canSeeAnalytics = currentUser.role === 'admin' || currentUser.role === 'teacher';
    const canFilterAnalytics = currentUser.role === 'admin';

    useEffect(() => {
        setIsLoading(true);
        const promises = [
            reportsAPI.getSummary(),
            reportsAPI.getStats(),
            reportsAPI.getTrialCount(),
            currentUser.role === 'admin' ? reportsAPI.getMostAbsent() : Promise.resolve([])
        ];

        Promise.all(promises)
            .then(([summaryRes, statsRes, trialRes, absentRes]) => {
                const summaryData = Array.isArray(summaryRes) ? summaryRes : (summaryRes?.data || []);
                const processedData = summaryData.map(item => ({
                    ...item,
                    total: parseInt(item.total || 0, 10),
                    present: parseInt(item.present || 0, 10),
                    absent: parseInt(item.absent || 0, 10),
                    late: parseInt(item.late || 0, 10),
                    day: item.day ? new Date(item.day).toLocaleDateString('zh-TW', { month: 'numeric', day: 'numeric', weekday: 'short' }) : 'N/A',
                }));
                setChartData(processedData);
                setStats(statsRes?.today !== undefined ? statsRes : (statsRes?.data || { today: 0, week: 0 }));
                setTrialCount(trialRes?.count !== undefined ? trialRes.count : (trialRes?.data?.count || 0));
                setMostAbsent(Array.isArray(absentRes) ? absentRes : (absentRes?.data || []));
            })
            .catch(err => console.error("Failed to fetch dashboard data:", err))
            .finally(() => setIsLoading(false));
    }, [currentUser.role]);

    // Fetch the teacher list for the comparison dropdown - only admins get
    // to switch whose data they're looking at (includes admins who also
    // teach, like Sule, so they appear as a selectable option too).
    useEffect(() => {
        if (!canFilterAnalytics) return;
        usersAPI.getAll().then(allUsers => {
            setTeachers(allUsers.filter(u => u.role === 'teacher' || u.role === 'admin'));
        }).catch(() => {});
    }, [canFilterAnalytics]);

    // Admin Analytics / "My Analytics" section is fetched separately so
    // switching the teacher filter doesn't reload the whole dashboard.
    useEffect(() => {
        if (!canSeeAnalytics) return;
        setAnalyticsLoading(true);
        const params = {};
        if (canFilterAnalytics && analyticsTeacherFilter) params.teacherId = analyticsTeacherFilter;
        reportsAPI.getSuperAdminStats(params)
            .then(superAdminRes => {
                if (superAdminRes) {
                    setSuperAdminStats(superAdminRes?.revenueProjection !== undefined ? superAdminRes : superAdminRes?.data);
                }
            })
            .catch(err => console.error("Failed to fetch analytics:", err))
            .finally(() => setAnalyticsLoading(false));
    }, [canSeeAnalytics, canFilterAnalytics, analyticsTeacherFilter]);

    if (isLoading) {
        return <div className="p-10"><Spinner /></div>;
    }


    return (
        <div className="space-y-6">
            {/* Stat Cards Section */}
            <div className="grid grid-cols-3 gap-2 sm:gap-4">
                <div className="bg-white dark:bg-gray-800 p-2.5 sm:p-5 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                    <h3 className="text-[9px] sm:text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider leading-tight">Today's Attendance</h3>
                    <p className="text-lg sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mt-0.5 sm:mt-1">{stats.today}%</p>
                </div>
                <div className="bg-white dark:bg-gray-800 p-2.5 sm:p-5 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                    <h3 className="text-[9px] sm:text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider leading-tight">Weekly Average</h3>
                    <p className="text-lg sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mt-0.5 sm:mt-1">{stats.week}%</p>
                </div>
                <div className="bg-white dark:bg-gray-800 p-2.5 sm:p-5 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                    <h3 className="text-[9px] sm:text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider leading-tight">Trial Students</h3>
                    <p className="text-lg sm:text-3xl font-bold text-purple-600 mt-0.5 sm:mt-1">{trialCount}</p>
                </div>
            </div>

            {/* Attendance Trend Chart & Frequent Absences */}
            <div className={`grid grid-cols-1 ${currentUser.role === 'admin' && mostAbsent.length > 0 ? 'lg:grid-cols-3' : ''} gap-6`}>
                <div className={`${currentUser.role === 'admin' && mostAbsent.length > 0 ? 'lg:col-span-2' : ''} bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700`}>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 mb-4">
                        <div>
                            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Attendance Trend (每日出勤趨勢)</h3>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Daily total attendance aggregated across all classes</p>
                        </div>
                    </div>
                    {chartData.length > 0 ? (
                        <div style={{ width: '100%', height: 380 }}>
                            <ResponsiveContainer>
                                <LineChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis dataKey="day" />
                                    <YAxis allowDecimals={false} />
                                    <Tooltip />
                                    <Legend />
                                    <Line type="monotone" dataKey="total" stroke="#6366F1" strokeWidth={2} strokeDasharray="4 4" name="Total (應到總計)" />
                                    <Line type="monotone" dataKey="present" stroke="#10B981" strokeWidth={2.5} name="Present (實到)" />
                                    <Line type="monotone" dataKey="absent" stroke="#EF4444" strokeWidth={2.5} name="Absent (缺席)" />
                                    <Line type="monotone" dataKey="late" stroke="#F59E0B" strokeWidth={2.5} name="Late (遲到)" />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    ) : <p className="text-gray-400 dark:text-gray-500 italic py-16 text-center">No attendance data available to display a chart.</p>}
                </div>

                {currentUser.role === 'admin' && mostAbsent.length > 0 && (
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 flex flex-col">
                        <h3 className="text-lg font-bold mb-2 text-gray-900 dark:text-gray-100 flex items-center gap-1.5">
                            <span>⚠️</span> Frequent Absences (近30天)
                        </h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Students with multiple absences requiring follow-up.</p>
                        <div className="overflow-y-auto max-h-[320px] space-y-2 flex-1">
                            {mostAbsent.map(student => (
                                <div key={student.name} className="flex justify-between items-center p-2.5 bg-red-50/60 rounded-lg border border-red-100">
                                    <span className="font-semibold text-sm text-gray-800 dark:text-gray-200">{student.name}</span>
                                    <span className="text-xs font-bold text-red-600 bg-red-100 px-2 py-0.5 rounded-full">{student.absent_count} absences</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Admin / My Analytics Section */}
            {canSeeAnalytics && superAdminStats && (
                <div className="space-y-6 border-t pt-6">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                            {(() => {
                                const meta = superAdminStats.meta;
                                if (!meta || meta.effectiveTeacherId === null) {
                                    return 'Admin Analytics (校務營收與運營分析)';
                                }
                                if (meta.isSelfScoped) {
                                    return language === 'zh' ? '我的分析 (我的營收與運營分析)' : 'My Analytics';
                                }
                                const viewedTeacher = teachers.find(tch => tch.id === meta.effectiveTeacherId);
                                const viewedName = viewedTeacher?.username || 'Teacher';
                                return language === 'zh' ? `${viewedName} 的分析` : `${viewedName}'s Analytics`;
                            })()}
                            {analyticsLoading && <span className="text-xs font-normal text-gray-400 dark:text-gray-500">loading…</span>}
                        </h2>
                        {canFilterAnalytics && (
                            <select
                                value={analyticsTeacherFilter}
                                onChange={e => setAnalyticsTeacherFilter(e.target.value)}
                                className="border border-gray-300 dark:border-gray-600 rounded-lg p-2 text-sm bg-white dark:bg-gray-800"
                            >
                                <option value="">
                                    {superAdminStats.meta?.hasOwnClasses ? 'My Classes (Default)' : 'All Teachers (Default)'}
                                </option>
                                <option value="all">All Teachers (Combined)</option>
                                {teachers.map(tch => (
                                    <option key={tch.id} value={tch.id}>{tch.username}</option>
                                ))}
                            </select>
                        )}
                    </div>

                    {/* 1. Comprehensive Monthly Revenue vs Current Date Income Card */}
                    {superAdminStats.monthlyRevenue ? (
                        <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 space-y-5">
                            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 dark:border-gray-700 pb-4">
                                <div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <h3 className="text-base font-bold text-gray-900 dark:text-gray-100">
                                            💰 {language === 'zh' ? '本月營收與達成追蹤' : 'Monthly Revenue & Income Tracking'}
                                        </h3>
                                        <span className="bg-blue-100 text-blue-800 text-xs px-2.5 py-0.5 rounded-full font-extrabold">
                                            {superAdminStats.monthlyRevenue.monthName}
                                        </span>
                                    </div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                        {language === 'zh' ? '預期應收總額 vs. 截至今日實得 (點名即計入實得課費)' : 'Possible Monthly Income vs. Realized Current Date Income'}
                                    </p>
                                </div>
                                <div className="flex items-center gap-2">
                                    {superAdminStats.monthlyRevenue.balanceStatus === 'balanced' ? (
                                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 border border-emerald-200 shadow-2xs">
                                            <span>🟢</span>
                                            <span>{language === 'zh' ? '全額達標 / 營收平衡' : 'Balanced / On Target'}</span>
                                        </span>
                                    ) : superAdminStats.monthlyRevenue.balanceStatus === 'lost' ? (
                                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-red-100 text-red-800 border border-red-200 shadow-2xs">
                                            <span>⚠️</span>
                                            <span>{language === 'zh' ? `缺課損失 (-NT$${superAdminStats.monthlyRevenue.lostRevenue.toLocaleString()})` : `Lost Revenue (-NT$${superAdminStats.monthlyRevenue.lostRevenue.toLocaleString()})`}</span>
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-purple-100 text-purple-800 border border-purple-200 shadow-2xs">
                                            <span>🚀</span>
                                            <span>{language === 'zh' ? `超額增課 (+NT$${superAdminStats.monthlyRevenue.balanceDiff.toLocaleString()})` : `Surplus (+NT$${superAdminStats.monthlyRevenue.balanceDiff.toLocaleString()})`}</span>
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Dual Primary Metric Cards (Clickable for In-Depth Analysis) */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                <button
                                    type="button"
                                    onClick={() => setActiveRevenueTab(prev => prev === 'possible' ? null : 'possible')}
                                    className={`p-4 bg-gradient-to-br from-blue-50/70 to-indigo-50/50 rounded-xl border text-left transition-all duration-200 cursor-pointer ${
                                        activeRevenueTab === 'possible' 
                                            ? 'ring-2 ring-blue-500 border-blue-400 shadow-md scale-[1.01]' 
                                            : 'border-blue-100 hover:border-blue-300 hover:shadow-sm'
                                    }`}
                                >
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs font-bold text-blue-700 uppercase tracking-wider block">
                                            {language === 'zh' ? '預期應收總額' : 'Possible Monthly Income'}
                                        </span>
                                        <span className="text-[10px] text-blue-600 bg-blue-100/80 px-1.5 py-0.5 rounded font-bold">
                                            {activeRevenueTab === 'possible' ? (language === 'zh' ? '▲ 收合' : '▲ Collapse') : (language === 'zh' ? '▼ 明細' : '▼ Details')}
                                        </span>
                                    </div>
                                    <span className="text-2xl font-black text-blue-950 mt-1 block">
                                        NT${superAdminStats.monthlyRevenue.possibleMonthlyIncome.toLocaleString()}
                                    </span>
                                    <span className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 block">
                                        {language === 'zh' ? '全月所有排定堂數全勤應收' : 'All scheduled sessions with 100% attendance'}
                                    </span>
                                    <div className="mt-2.5 pt-2 border-t border-blue-200/50 flex items-center justify-between text-[11px] font-semibold text-blue-700">
                                        <span>{language === 'zh' ? '點擊檢視各班預估' : 'Click for class breakdown'}</span>
                                        <span>➔</span>
                                    </div>
                                </button>

                                <button
                                    type="button"
                                    onClick={() => setActiveRevenueTab(prev => prev === 'current' ? null : 'current')}
                                    className={`p-4 bg-gradient-to-br from-emerald-50/70 to-teal-50/50 rounded-xl border text-left transition-all duration-200 cursor-pointer ${
                                        activeRevenueTab === 'current' 
                                            ? 'ring-2 ring-emerald-500 border-emerald-400 shadow-md scale-[1.01]' 
                                            : 'border-emerald-100 hover:border-emerald-300 hover:shadow-sm'
                                    }`}
                                >
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs font-bold text-emerald-700 uppercase tracking-wider block">
                                            {language === 'zh' ? '截至今日實得' : 'Current Date Income'}
                                        </span>
                                        <span className="text-[10px] text-emerald-700 bg-emerald-100/80 px-1.5 py-0.5 rounded font-bold">
                                            {activeRevenueTab === 'current' ? (language === 'zh' ? '▲ 收合' : '▲ Collapse') : (language === 'zh' ? '▼ 明細' : '▼ Details')}
                                        </span>
                                    </div>
                                    <span className="text-2xl font-black text-emerald-700 mt-1 block">
                                        NT${superAdminStats.monthlyRevenue.currentDateIncome.toLocaleString()}
                                    </span>
                                    <span className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 block">
                                        {language === 'zh' ? `已點名堂數實得課費 (${superAdminStats.monthlyRevenue.realizedPercentage}% 達成率)` : `Sessions held & recorded (${superAdminStats.monthlyRevenue.realizedPercentage}% realized)`}
                                    </span>
                                    <div className="mt-2.5 pt-2 border-t border-emerald-200/50 flex items-center justify-between text-[11px] font-semibold text-emerald-700">
                                        <span>{language === 'zh' ? '點擊檢視已上課紀錄' : 'Click for session logs'}</span>
                                        <span>➔</span>
                                    </div>
                                </button>

                                <button
                                    type="button"
                                    onClick={() => setActiveRevenueTab(prev => prev === 'lost' ? null : 'lost')}
                                    className={`p-4 bg-gradient-to-br from-red-50/70 to-orange-50/50 rounded-xl border text-left transition-all duration-200 cursor-pointer ${
                                        activeRevenueTab === 'lost' 
                                            ? 'ring-2 ring-red-500 border-red-400 shadow-md scale-[1.01]' 
                                            : 'border-red-100 hover:border-red-300 hover:shadow-sm'
                                    }`}
                                >
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs font-bold text-red-700 uppercase tracking-wider block">
                                            {language === 'zh' ? '缺課損失' : 'Lost Revenue'}
                                        </span>
                                        <span className="text-[10px] text-red-700 bg-red-100/80 px-1.5 py-0.5 rounded font-bold">
                                            {activeRevenueTab === 'lost' ? (language === 'zh' ? '▲ 收合' : '▲ Collapse') : (language === 'zh' ? '▼ 安排補課' : '▼ Make-Up')}
                                        </span>
                                    </div>
                                    <span className="text-2xl font-black text-red-600 mt-1 block">
                                        NT${superAdminStats.monthlyRevenue.lostRevenue.toLocaleString()}
                                    </span>
                                    <span className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 block">
                                        {language === 'zh' ? '學生請假未上課短收 (安排補課可補回)' : 'Absent sessions without billing (recover via make-up)'}
                                    </span>
                                    <div className="mt-2.5 pt-2 border-t border-red-200/50 flex items-center justify-between text-[11px] font-semibold text-red-700">
                                        <span>{language === 'zh' ? '點擊檢視損失與補課' : 'Click to inspect & make-up'}</span>
                                        <span>➔</span>
                                    </div>
                                </button>

                                <button
                                    type="button"
                                    onClick={() => setActiveRevenueTab(prev => prev === 'remaining' ? null : 'remaining')}
                                    className={`p-4 bg-gradient-to-br from-gray-50 to-slate-100 rounded-xl border text-left transition-all duration-200 cursor-pointer ${
                                        activeRevenueTab === 'remaining' 
                                            ? 'ring-2 ring-slate-500 border-slate-400 shadow-md scale-[1.01]' 
                                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 hover:shadow-sm'
                                    }`}
                                >
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider block">
                                            {language === 'zh' ? '本月剩餘預估' : 'Remaining Projected'}
                                        </span>
                                        <span className="text-[10px] text-slate-700 bg-slate-200/80 px-1.5 py-0.5 rounded font-bold">
                                            {activeRevenueTab === 'remaining' ? (language === 'zh' ? '▲ 收合' : '▲ Collapse') : (language === 'zh' ? '▼ 未來排課' : '▼ Pipeline')}
                                        </span>
                                    </div>
                                    <span className="text-2xl font-black text-gray-800 dark:text-gray-200 mt-1 block">
                                        NT${superAdminStats.monthlyRevenue.remainingProjected.toLocaleString()}
                                    </span>
                                    <span className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 block">
                                        {language === 'zh' ? `未來未上堂數預估 (剩餘 ${superAdminStats.monthlyRevenue.daysRemaining} 天)` : `Future scheduled sessions (${superAdminStats.monthlyRevenue.daysRemaining} days left)`}
                                    </span>
                                    <div className="mt-2.5 pt-2 border-t border-gray-200/50 flex items-center justify-between text-[11px] font-semibold text-gray-700 dark:text-gray-300">
                                        <span>{language === 'zh' ? '點擊檢視預計上課清單' : 'Click for upcoming pipeline'}</span>
                                        <span>➔</span>
                                    </div>
                                </button>
                            </div>

                            {/* Realization Progress Bar */}
                            <div className="space-y-2">
                                <div className="flex justify-between text-xs font-semibold text-gray-600 dark:text-gray-400">
                                    <span>{language === 'zh' ? '營收實現進度' : 'Revenue Realization'}</span>
                                    <span>{superAdminStats.monthlyRevenue.realizedPercentage}% {language === 'zh' ? '已實現' : 'Realized'}</span>
                                </div>
                                <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-3 overflow-hidden flex">
                                    <div 
                                        className="bg-emerald-500 h-full transition-all duration-500" 
                                        style={{ width: `${Math.min(100, superAdminStats.monthlyRevenue.realizedPercentage)}%` }}
                                        title={`Realized: NT$${superAdminStats.monthlyRevenue.currentDateIncome}`}
                                    />
                                    {superAdminStats.monthlyRevenue.possibleMonthlyIncome > 0 && superAdminStats.monthlyRevenue.lostRevenue > 0 && (
                                        <div 
                                            className="bg-red-400 h-full transition-all duration-500" 
                                            style={{ width: `${Math.min(100, (superAdminStats.monthlyRevenue.lostRevenue / superAdminStats.monthlyRevenue.possibleMonthlyIncome) * 100)}%` }}
                                            title={`Lost: NT$${superAdminStats.monthlyRevenue.lostRevenue}`}
                                        />
                                    )}
                                </div>
                                <div className="flex flex-wrap items-center justify-between text-[11px] text-gray-500 dark:text-gray-400 gap-2 pt-1">
                                    <div className="flex items-center gap-4 flex-wrap">
                                        <span className="flex items-center gap-1.5">
                                            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 inline-block" />
                                            {language === 'zh' ? '實得課費' : 'Realized'}: NT${superAdminStats.monthlyRevenue.currentDateIncome.toLocaleString()}
                                        </span>
                                        <span className="flex items-center gap-1.5">
                                            <span className="w-2.5 h-2.5 rounded-full bg-red-400 inline-block" />
                                            {language === 'zh' ? '缺課未收' : 'Lost'}: NT${superAdminStats.monthlyRevenue.lostRevenue.toLocaleString()}
                                        </span>
                                        <span className="flex items-center gap-1.5">
                                            <span className="w-2.5 h-2.5 rounded-full bg-gray-300 inline-block" />
                                            {language === 'zh' ? '剩餘未上' : 'Remaining'}: NT${superAdminStats.monthlyRevenue.remainingProjected.toLocaleString()}
                                        </span>
                                    </div>
                                    <div className="font-bold text-gray-700 dark:text-gray-300">
                                        {language === 'zh' ? '實收入帳' : 'Confirmed Paid'}: <span className="text-emerald-700 font-extrabold">NT${superAdminStats.monthlyRevenue.collectedIncome.toLocaleString()}</span>
                                        {superAdminStats.monthlyRevenue.uncollectedDue > 0 && (
                                            <span className="text-amber-700 ml-2 font-medium">({language === 'zh' ? '待收待結' : 'Pending'}: NT${superAdminStats.monthlyRevenue.uncollectedDue.toLocaleString()})</span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* In-Depth Breakdown Drawer/Panel */}
                            {activeRevenueTab && (
                                <div className="pt-2 border-t border-gray-100 dark:border-gray-700">
                                    <MonthlyRevenueBreakdown
                                        activeTab={activeRevenueTab}
                                        onSelectTab={setActiveRevenueTab}
                                        onClose={() => setActiveRevenueTab(null)}
                                        monthlyRevenue={superAdminStats.monthlyRevenue}
                                        language={language}
                                        t={t}
                                    />
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                            <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">{language === 'zh' ? '月度預估總收入' : 'Monthly Revenue Projection'}</h3>
                            <p className="text-3xl font-extrabold text-emerald-600">NT${superAdminStats.revenueProjection}</p>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{language === 'zh' ? '根據目前在讀學生數與鐘點費預估' : 'Based on current active enrollments and rates.'}</p>
                        </div>
                    )}

                    {/* 2. Teacher Performance & Student Retention */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Teacher Performance */}
                        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                            <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">{language === 'zh' ? '教師出席率' : 'Teacher Attendance Rate'}</h3>
                            <ul className="space-y-2">
                                {superAdminStats.teacherPerformance.map(teacher => (
                                    <li key={teacher.teacher_name} className="flex justify-between items-center text-sm p-2 bg-gray-50 dark:bg-gray-800/60 rounded-lg">
                                        <span className="font-medium text-gray-800 dark:text-gray-200">{teacher.teacher_name}</span>
                                        <span className="font-bold text-emerald-600">{teacher.attendance_rate}%</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Student Retention */}
                        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                            <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">{language === 'zh' ? '近30天家教學生留存' : 'Tutoring Student Retention (30 Days)'}</h3>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between items-center p-2 bg-gray-50 dark:bg-gray-800/60 rounded-lg">
                                    <span className="text-gray-600 dark:text-gray-400">{language === 'zh' ? '活躍學生總數:' : 'Total Active:'}</span>
                                    <span className="font-bold text-gray-900 dark:text-gray-100">{superAdminStats.studentRetention.totalActive}</span>
                                </div>
                                <div className="flex justify-between items-center p-2 bg-gray-50 dark:bg-gray-800/60 rounded-lg">
                                    <span className="text-gray-600 dark:text-gray-400">{language === 'zh' ? '新加入學生:' : 'New Students:'}</span>
                                    <span className="font-bold text-blue-600">+{superAdminStats.studentRetention.new}</span>
                                </div>
                                <div className="flex justify-between items-center p-2 bg-gray-50 dark:bg-gray-800/60 rounded-lg">
                                    <span className="text-gray-600 dark:text-gray-400">{language === 'zh' ? '穩定在讀學生:' : 'Retained Students:'}</span>
                                    <span className="font-bold text-emerald-600">{superAdminStats.studentRetention.retained}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}